import { ApiResponse as APIResponse } from '../../shared/types';
import {
  EbmSaleRecordsFilterParams,
  PaginatedResponse,
} from '../types/pagination';
import { supabase } from './supabase';

const DEFAULT_PAGE_SIZE = 20;
const DEFAULT_STATUSES = ['FAILED', 'PENDING'];

export interface EbmSettlementSummary {
  id: string;
  amount: number;
  payment_method_id: number;
  payment_method_name: string;
  created_at: string;
}

export interface EbmSaleRecord {
  id: string;
  outlet_id: string;
  order_id: string;
  order_bill_number: number;
  order_date: string;
  invc_no: number;
  submission_status:
    | 'PENDING'
    | 'SUBMITTED_QUEUED'
    | 'CONFIRMED'
    | 'FAILED'
    | 'OVERRIDDEN_UNCONFIRMED';
  rcpt_no: string | null;
  tot_rcpt_no: number | null;
  rcpt_ty_cd: 'S' | 'R';
  sdc_id: string | null;
  mrc_no: string | null;
  rcpt_sign: string | null;
  intrl_data: string | null;
  vsdc_rcpt_pbct_date: string | null;
  result_cd: string | null;
  error_message: string | null;
  requires_reprint: boolean;
  submitted_at: string | null;
  confirmed_at: string | null;
  created_at: string;
  updated_at: string;
  settlements: EbmSettlementSummary[];
}

export const fetchEbmSaleRecords = async ({
  outletId,
  submissionStatus,
  search,
  page = 1,
  pageSize = DEFAULT_PAGE_SIZE,
}: EbmSaleRecordsFilterParams): Promise<
  APIResponse<PaginatedResponse<EbmSaleRecord>>
> => {
  try {
    const from = (page - 1) * pageSize;
    const to = from + pageSize - 1;

    let query = supabase
      .from('ebm_sale_records_view')
      .select('*', { count: 'exact' })
      .in('submission_status', submissionStatus ?? DEFAULT_STATUSES)
      .order('created_at', { ascending: false })
      .range(from, to);

    if (outletId) {
      query = query.eq('outlet_id', outletId);
    }

    if (search) {
      query = query.eq('order_bill_number', Number(search) || -1);
    }

    const { data, error, count } = await query;

    if (error) {
      return { success: false, error: error.message, data: undefined };
    }

    const totalItems = count || 0;
    const totalPages = Math.ceil(totalItems / pageSize);

    return {
      success: true,
      data: {
        data: (data || []) as EbmSaleRecord[],
        pagination: {
          page,
          pageSize,
          totalItems,
          totalPages,
          hasNextPage: page < totalPages,
          hasPreviousPage: page > 1,
        },
      },
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch EBM sale records',
      data: undefined,
    };
  }
};

export interface FiscalReceiptLineItem {
  name: string;
  quantity: number;
  amount: number;
  taxCode: string | null;
  taxRate: number | null;
}

// Used to build a printable fiscal receipt for a CONFIRMED sale record —
// ebm_sale_records itself only stores the SDC response fields, not an
// itemized tax breakdown, so this re-derives it from order_items the same
// way saleSubmission.ts does at submission time (via a direct Supabase read,
// since this page is online-only and the order may belong to an outlet
// other than the one currently connected locally on this device).
export const fetchFiscalReceiptLineItems = async (
  orderId: string,
): Promise<APIResponse<FiscalReceiptLineItem[]>> => {
  try {
    const { data, error } = await supabase
      .from('order_items')
      .select(
        'quantity, amount, menu_items(name, fiscal_tax_categories(code, default_rate))',
      )
      .eq('order_id', orderId)
      .neq('status', 'CANCELLED');

    if (error) {
      return { success: false, error: error.message, data: [] };
    }

    return {
      success: true,
      data: (data || []).map((row: any) => ({
        name: row.menu_items?.name ?? 'Item',
        quantity: Number(row.quantity),
        amount: Number(row.amount),
        taxCode: row.menu_items?.fiscal_tax_categories?.code ?? null,
        taxRate:
          row.menu_items?.fiscal_tax_categories?.default_rate != null
            ? Number(row.menu_items.fiscal_tax_categories.default_rate)
            : null,
      })),
    };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch order items for receipt',
      data: [],
    };
  }
};

export interface LatestSettlementOnline {
  id: string;
  payment_method_id: number;
}

// Online equivalent of localFetchLatestSettlement — needed because a
// settled order's order_settlements row is pruned from local SQLite the
// moment status_id moves off OPEN (powersync.yaml's outlet_order_children
// stream is status_id = 9 only), so the local read always misses for any
// record reachable from this (retry) page.
export const fetchLatestSettlementOnline = async (
  orderId: string,
): Promise<APIResponse<LatestSettlementOnline | null>> => {
  try {
    const { data, error } = await supabase
      .from('order_settlements')
      .select('id, payment_method_id')
      .eq('order_id', orderId)
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
    if (error) {
      return { success: false, error: error.message, data: null };
    }
    return { success: true, data: data as LatestSettlementOnline | null };
  } catch (error) {
    return {
      success: false,
      error:
        error instanceof Error
          ? error.message
          : 'Failed to fetch settlement for retry',
      data: null,
    };
  }
};
